
import React, { useState } from 'react';
import { generatePersonalizedOptimization, type BuyingPowerData } from '../utils/optimizationIntelligence';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, Target, CreditCard, DollarSign, Home, Receipt, BarChart3 } from 'lucide-react';
import { PITIComparisonCard } from '../../comparison/PITIComparisonCard';
import type { MortgageData } from '../../CalculatorTabs';
import { formatCurrency } from '@/components/calculator/utils/formatters';

interface OptimizationSuggestionsProps {
  selectedTerm: string;
  mortgageData: MortgageData;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'payment': return <DollarSign className="w-4 h-4" />;
    case 'refinance': return <TrendingUp className="w-4 h-4" />;
    case 'credit': return <CreditCard className="w-4 h-4" />;
    case 'equity': return <Home className="w-4 h-4" />;
    case 'tax': return <Receipt className="w-4 h-4" />;
    default: return <Target className="w-4 h-4" />;
  }
};

const getConfidenceColor = (confidence: string) => {
  switch (confidence) {
    case 'high': return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    case 'low': return 'bg-red-500/20 text-red-300 border-red-500/30';
    default: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
  }
};

export const OptimizationSuggestions: React.FC<OptimizationSuggestionsProps> = ({ selectedTerm, mortgageData }) => {
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(null);
  
  // Estimate financial capacity from mortgage payment using standard 28% front-end ratio
  const estimatedMonthlyIncome = mortgageData.monthlyPayment.total / 0.28;
  const estimatedTotalMonthlyDebt = mortgageData.monthlyPayment.total * 0.15; // Conservative estimate
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  
  // Create buying power data from calculator inputs
  const buyingPowerData: BuyingPowerData = {
    totalMonthlyIncome: estimatedMonthlyIncome,
    totalMonthlyDebt: estimatedTotalMonthlyDebt,
    maxBuyingPower: mortgageData.homePrice,
    debtToIncomeRatio: (mortgageData.monthlyPayment.total + estimatedTotalMonthlyDebt) / estimatedMonthlyIncome,
    creditScore: mortgageData.creditScore || 720,
    downPaymentPercentage: mortgageData.downPaymentPercentage || 20,
    state: mortgageData.state || 'CA',
    loanType: mortgageData.loanType || 'conventional'
  };

  const optimization = generatePersonalizedOptimization(mortgageData, buyingPowerData, selectedTerm);

  return (
    <div className="bg-slate-900/80 border border-slate-700 rounded-xl px-6 py-4 space-y-4">
      <div className="font-bold text-slate-200 mb-4 flex gap-2 items-center">
        <span className="">◎</span>
        <span>Personalized Optimization Analysis</span>
      </div>

      {/* Disclaimer about estimation */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
        <p className="text-xs text-yellow-300">
          ℹ️ Analysis based on estimated financial capacity from your {formatCurrency(mortgageData.monthlyPayment.total)}/month mortgage payment. 
          For more accurate recommendations, use the Buying Power Calculator to input your actual income and debt details.
        </p>
      </div>

      {/* Key Insights Summary */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-blue-400" />
            <span className="font-semibold text-blue-300">Smart Extra Payments</span>
          </div>
          <div className="text-sm text-slate-300">
            <div>Recommended: <span className="text-blue-300 font-medium">{formatCurrency(optimization.extraPaymentAnalysis.recommendedAmount)}/month</span></div>
            <div>Interest Savings: <span className="text-green-400 font-medium">{formatCurrency(optimization.extraPaymentAnalysis.interestSavings)}</span></div>
            <div>Years Saved: <span className="text-purple-400 font-medium">{optimization.extraPaymentAnalysis.yearsReduced} years</span></div>
          </div>
        </div>

        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <span className="font-semibold text-green-300">Refinancing Analysis</span>
          </div>
          <div className="text-sm text-slate-300">
            {optimization.refinancingAnalysis.isRecommended ? (
              <>
                <div>Potential Savings: <span className="text-green-400 font-medium">{formatCurrency(optimization.refinancingAnalysis.monthlySavings)}/month</span></div>
                <div>Break-even: <span className="text-blue-400 font-medium">{optimization.refinancingAnalysis.breakEvenPoint} months</span></div>
                <div className="text-green-400 text-xs mt-1">✓ Recommended based on your profile</div>
              </>
            ) : (
              <div className="text-yellow-400">Not recommended at current rates</div>
            )}
          </div>
        </div>
      </div>

      {/* Credit Score Impact */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-5 h-5 text-purple-400" />
          <span className="font-semibold text-purple-300">Credit Score Impact</span>
          <Badge variant="outline" className="text-xs">{buyingPowerData.creditScore}</Badge>
        </div>
        <div className="text-sm text-slate-300">
          <div className="mb-1">{optimization.creditOptimization.currentImpact}</div>
          <div className="text-blue-300">{optimization.creditOptimization.potentialImprovement}</div>
          <div className="text-xs text-slate-400 mt-1">Timeline: {optimization.creditOptimization.timeframe}</div>
        </div>
      </div>

      {/* Personalized Strategies */}
      <div className="space-y-3">
        <h4 className="font-semibold text-slate-200 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Priority Strategies for Your Situation
        </h4>
        {optimization.strategies.map((strategy, index) => (
          <div key={index} className={`border rounded-lg p-4 ${getConfidenceColor(strategy.confidence)}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {getCategoryIcon(strategy.category)}
                <span className="font-semibold text-sm">{strategy.title}</span>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">#{strategy.priority}</Badge>
                <Badge variant="outline" className={`text-xs ${strategy.confidence === 'high' ? 'border-green-500/50 text-green-300' : strategy.confidence === 'medium' ? 'border-yellow-500/50 text-yellow-300' : 'border-red-500/50 text-red-300'}`}>
                  {strategy.confidence}
                </Badge>
                {strategy.pitiComparison && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedStrategy(expandedStrategy === index ? null : index)}
                    className="h-6 px-2 text-xs"
                  >
                    <BarChart3 className="w-3 h-3 mr-1" />
                    {expandedStrategy === index ? 'Hide' : 'Show'} PITI
                  </Button>
                )}
              </div>
            </div>
            <div className="text-sm mb-2">{strategy.description}</div>
            <div className="flex flex-wrap gap-4 text-xs">
              <div><span className="text-slate-400">Impact:</span> <span className="font-medium">{strategy.impact}</span></div>
              <div><span className="text-slate-400">Timeline:</span> <span className="font-medium">{strategy.savings}</span></div>
            </div>
            
            {/* PITI Comparison */}
            {strategy.pitiComparison && expandedStrategy === index && (
              <div className="mt-4">
                <PITIComparisonCard
                  comparison={strategy.pitiComparison}
                  title={`${strategy.title} - Payment Impact`}
                  description="See exactly how this strategy affects your monthly payment breakdown"
                  className="border-0 bg-slate-800/30"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Financial Context */}
      <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4 mt-4">
        <div className="text-xs text-slate-400 space-y-1">
          <div>Analysis based on: {formatCurrency(buyingPowerData.totalMonthlyIncome)}/month estimated income, {Math.round(buyingPowerData.debtToIncomeRatio * 100)}% DTI, {buyingPowerData.creditScore} credit score</div>
          <div>Available monthly cash flow: ~{formatCurrency(Math.max(0, buyingPowerData.totalMonthlyIncome - buyingPowerData.totalMonthlyDebt - mortgageData.monthlyPayment.total))}</div>
        </div>
      </div>
    </div>
  );
};
